import { Component, OnInit } from '@angular/core';
import { AgentCatalogService, AgentCatalogEntry } from './agent-catalog.service';

@Component({
  selector: 'app-agent-catalog',
  templateUrl: './agent-catalog.component.html',
  styleUrls: ['./agent-catalog.component.css'],
})
export class AgentCatalogComponent implements OnInit {
  agents: AgentCatalogEntry[] = [];
  filtered: AgentCatalogEntry[] = [];
  selected: AgentCatalogEntry | null = null;
  searchQuery = '';
  loading = true;
  activeTab: 'catalog' | 'graph' = 'catalog';

  // ── Graph node layout ──────────────────────────────────────────────────────
  readonly GRAPH_W = 960;
  readonly GRAPH_H = 680;

  readonly llmNodes = [
    { id: 'claude',     label: 'Claude',     x: 760, y: 60,  color: '#7C3AED', icon: '🤖' },
    { id: 'grok',       label: 'Grok',       x: 860, y: 180, color: '#0284C7', icon: '🤖' },
    { id: 'openai',     label: 'OpenAI',     x: 880, y: 320, color: '#059669', icon: '🤖' },
    { id: 'copilot',    label: 'Copilot',    x: 830, y: 450, color: '#0369A1', icon: '🤖' },
    { id: 'ollama',     label: 'Ollama',     x: 740, y: 560, color: '#64748B', icon: '🤖' },
    { id: 'openrouter', label: 'OpenRouter', x: 620, y: 620, color: '#92400E', icon: '🤖' },
  ];

  readonly apiNodes = [
    { id: 'websearch',   label: 'Web Search',    x: 60,  y: 60,  color: '#0284C7', icon: '🔍' },
    { id: 'huggingface', label: 'HuggingFace',   x: 60,  y: 170, color: '#DC2626', icon: '🎨' },
    { id: 'smtp',        label: 'SMTP Email',    x: 60,  y: 280, color: '#0F766E', icon: '✉️' },
    { id: 'claudecode',  label: 'Claude Code',   x: 60,  y: 390, color: '#7C3AED', icon: '💻' },
    { id: 'projectdb',   label: 'Project DB',    x: 60,  y: 500, color: '#D97706', icon: '🗄️' },
    { id: 'svg_engine',  label: 'SVG Engine',    x: 60,  y: 610, color: '#EC4899', icon: '🖼️' },
  ];

  // Layer 1 agents arranged in an arc
  readonly agentNodes = [
    { id: 'OrchestratorAgent', x: 480, y: 340, layer: 0, color: '#7C3AED', icon: 'hub' },
    { id: 'ResearchAgent',     x: 340, y: 160, layer: 1, color: '#0284C7', icon: 'search' },
    { id: 'AnalysisAgent',     x: 480, y: 130, layer: 1, color: '#0891B2', icon: 'analytics' },
    { id: 'EngineeringAgent',  x: 620, y: 160, layer: 1, color: '#059669', icon: 'code' },
    { id: 'DatabaseAgent',     x: 700, y: 260, layer: 1, color: '#D97706', icon: 'storage' },
    { id: 'MarketingAgent',    x: 680, y: 400, layer: 1, color: '#DC2626', icon: 'campaign' },
    { id: 'DeliveryAgent',     x: 580, y: 490, layer: 1, color: '#0F766E', icon: 'send' },
    { id: 'WritingAgent',      x: 400, y: 490, layer: 1, color: '#7C3AED', icon: 'edit_note' },
    { id: 'ReviewAgent',       x: 290, y: 420, layer: 1, color: '#9333EA', icon: 'fact_check' },
    { id: 'SchedulingAgent',   x: 260, y: 300, layer: 1, color: '#0369A1', icon: 'calendar_today' },
    { id: 'FinancialAgent',    x: 300, y: 200, layer: 1, color: '#16A34A', icon: 'attach_money' },
    { id: 'ParalegalAgent',    x: 570, y: 210, layer: 1, color: '#92400E', icon: 'gavel' },
    { id: 'IT_PMAgent',        x: 450, y: 210, layer: 1, color: '#475569', icon: 'engineering' },
    { id: 'CoachAgent',        x: 440, y: 470, layer: 1, color: '#EA580C', icon: 'school' },
    { id: 'ScrutinizerAgent',     x: 530, y: 450, layer: 1, color: '#BE123C', icon: 'policy' },
    { id: 'ToolsAgent',           x: 640, y: 470, layer: 1, color: '#64748B', icon: 'build' },
    { id: 'GraphicDesignerAgent', x: 720, y: 380, layer: 1, color: '#EC4899', icon: 'palette' },
  ];

  // Edges: orchestrator→specialists
  readonly pipelineEdges = [
    'OrchestratorAgent→ResearchAgent',
    'OrchestratorAgent→AnalysisAgent',
    'OrchestratorAgent→EngineeringAgent',
    'OrchestratorAgent→DatabaseAgent',
    'OrchestratorAgent→MarketingAgent',
    'OrchestratorAgent→DeliveryAgent',
    'OrchestratorAgent→WritingAgent',
    'OrchestratorAgent→ReviewAgent',
    'OrchestratorAgent→SchedulingAgent',
    'OrchestratorAgent→FinancialAgent',
    'OrchestratorAgent→ParalegalAgent',
    'OrchestratorAgent→IT_PMAgent',
    'OrchestratorAgent→CoachAgent',
    'OrchestratorAgent→ScrutinizerAgent',
    'OrchestratorAgent→ToolsAgent',
    'OrchestratorAgent→GraphicDesignerAgent',
    'ResearchAgent→GraphicDesignerAgent',
    'ResearchAgent→AnalysisAgent',
    'AnalysisAgent→WritingAgent',
    'WritingAgent→ReviewAgent',
    'ReviewAgent→DeliveryAgent',
    'EngineeringAgent→ReviewAgent',
    'DatabaseAgent→EngineeringAgent',
  ];

  // Which agents connect to which external system
  readonly llmEdges: { from: string; to: string }[] = [
    { from: 'ResearchAgent',    to: 'claude' },
    { from: 'AnalysisAgent',    to: 'claude' },
    { from: 'EngineeringAgent', to: 'claude' },
    { from: 'DatabaseAgent',    to: 'claude' },
    { from: 'MarketingAgent',   to: 'claude' },
    { from: 'WritingAgent',     to: 'claude' },
    { from: 'FinancialAgent',   to: 'claude' },
    { from: 'ParalegalAgent',   to: 'claude' },
    { from: 'SchedulingAgent',  to: 'claude' },
    { from: 'IT_PMAgent',       to: 'claude' },
    { from: 'CoachAgent',       to: 'claude' },
    { from: 'ScrutinizerAgent',     to: 'claude' },
    { from: 'GraphicDesignerAgent', to: 'claude' },
    { from: 'OrchestratorAgent',    to: 'claude' },
    { from: 'EngineeringAgent', to: 'grok' },
    { from: 'MarketingAgent',   to: 'grok' },
    { from: 'ResearchAgent',    to: 'openai' },
    { from: 'WritingAgent',     to: 'openai' },
    { from: 'EngineeringAgent', to: 'copilot' },
    { from: 'ToolsAgent',       to: 'ollama' },
    { from: 'ResearchAgent',    to: 'openrouter' },
  ];

  readonly apiEdges: { from: string; to: string }[] = [
    { from: 'ResearchAgent',    to: 'websearch' },
    { from: 'MarketingAgent',   to: 'huggingface' },
    { from: 'DeliveryAgent',    to: 'smtp' },
    { from: 'EngineeringAgent', to: 'claudecode' },
    { from: 'DatabaseAgent',    to: 'claudecode' },
    { from: 'MarketingAgent',         to: 'claudecode' },
    { from: 'GraphicDesignerAgent',   to: 'huggingface' },
    { from: 'GraphicDesignerAgent',   to: 'svg_engine' },
    { from: 'DatabaseAgent',          to: 'projectdb' },
    { from: 'ToolsAgent',             to: 'projectdb' },
  ];

  highlightedAgent: string | null = null;

  constructor(private catalogService: AgentCatalogService) {}

  ngOnInit(): void {
    this.catalogService.getAll().subscribe({
      next: (data) => {
        this.agents = data;
        this.filtered = data;
        this.selected = data[0] ?? null;
        this.loading = false;
      },
      error: () => { this.loading = false; },
    });
  }

  onSearch(query: string): void {
    const q = query.toLowerCase();
    this.filtered = this.agents.filter(a =>
      a.name.toLowerCase().includes(q) ||
      a.role.toLowerCase().includes(q) ||
      a.description.toLowerCase().includes(q) ||
      a.knowledgeDomains.some(d => d.toLowerCase().includes(q))
    );
  }

  select(agent: AgentCatalogEntry): void {
    this.selected = agent;
  }

  layerLabel(layer: number): string {
    return layer === 0 ? 'Orchestrator' : layer === 1 ? 'Specialist' : 'Sub-Agent';
  }

  layerColor(layer: number): string {
    return layer === 0 ? '#7C3AED' : layer === 1 ? '#0284C7' : '#64748B';
  }

  // ── Graph helpers ──────────────────────────────────────────────────────────
  nodeById(id: string) {
    return this.agentNodes.find(n => n.id === id)
      ?? this.llmNodes.find(n => n.id === id)
      ?? this.apiNodes.find(n => n.id === id);
  }

  edgeLine(fromId: string, toId: string): string {
    const a = this.nodeById(fromId);
    const b = this.nodeById(toId);
    if (!a || !b) return '';
    return `M${a.x},${a.y} L${b.x},${b.y}`;
  }

  parsePipelineEdge(e: string): { from: string; to: string } {
    const [from, to] = e.split('→');
    return { from, to };
  }

  isHighlighted(nodeId: string): boolean {
    if (!this.highlightedAgent) return false;
    const connected = new Set<string>([this.highlightedAgent]);
    this.pipelineEdges.forEach(e => {
      const { from, to } = this.parsePipelineEdge(e);
      if (from === this.highlightedAgent) connected.add(to);
      if (to   === this.highlightedAgent) connected.add(from);
    });
    this.llmEdges.forEach(e => {
      if (e.from === this.highlightedAgent) connected.add(e.to);
    });
    this.apiEdges.forEach(e => {
      if (e.from === this.highlightedAgent) connected.add(e.to);
    });
    return connected.has(nodeId);
  }

  edgeHighlighted(fromId: string, toId: string): boolean {
    if (!this.highlightedAgent) return false;
    return fromId === this.highlightedAgent || toId === this.highlightedAgent;
  }

  clickGraphNode(nodeId: string): void {
    this.highlightedAgent = this.highlightedAgent === nodeId ? null : nodeId;
    const match = this.agents.find(a => a.name === nodeId);
    if (match) {
      this.selected = match;
      this.activeTab = 'catalog';
    }
  }

  graphNodeOpacity(nodeId: string): number {
    if (!this.highlightedAgent) return 1;
    return this.isHighlighted(nodeId) ? 1 : 0.25;
  }
}
